// Pure token utility functions — no side effects, no DOM access.
//
// Moved here from cloud/dp/shell/src/logic/token-utils.ts so that the
// platform shell, dev-shell fixtures, and any future app can all share
// one canonical JWT-decode + claim-extraction implementation. This file
// is the lower layer of the auth stack; `token-manager.ts` builds the
// browser-storage + refresh-scheduler on top, and `auth-context.tsx`
// wraps both for React.
import { appPermissionKeysOf } from '@zion/sdk-contracts';
/** Decode the payload section of a JWT without verifying the signature. */
export function parseJwt(token) {
    const parts = token.split('.');
    if (parts.length !== 3)
        throw new Error('Invalid JWT format');
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
}
/** Check whether a token is expired (or will expire within bufferMs). */
export function isTokenExpired(expiresAt, bufferMs = 0) {
    if (expiresAt === null)
        return true;
    return Date.now() >= expiresAt - bufferMs;
}
/** Get milliseconds until token expires. Returns 0 if already expired or null. */
export function getTimeUntilExpiry(expiresAt) {
    if (expiresAt === null)
        return 0;
    return Math.max(0, expiresAt - Date.now());
}
const EMPTY_CLAIMS = {
    sub: null,
    email: null,
    orgId: null,
    orgRoles: [],
    orgPermissions: [],
    siteRoles: [],
    groups: [],
    ring: null,
    customerChannelId: null,
    customerChannelName: null,
    appPermissionKeys: [],
};
/** Extract claims from an access token, returning safe defaults on failure. */
export function extractClaims(accessToken) {
    if (!accessToken)
        return EMPTY_CLAIMS;
    try {
        const payload = parseJwt(accessToken);
        return {
            sub: payload.sub ?? null,
            email: payload.email ?? null,
            orgId: payload.orgId ?? null,
            orgRoles: payload.orgRoles ?? [],
            orgPermissions: payload.orgPermissions ?? [],
            siteRoles: payload.siteRoles ?? [],
            groups: payload.groups ?? [],
            ring: payload.ring ?? null,
            customerChannelId: payload.customerChannelId ?? null,
            customerChannelName: payload.customerChannelName ?? null,
            appPermissionKeys: appPermissionKeysOf(payload.appPermissions),
        };
    }
    catch {
        return EMPTY_CLAIMS;
    }
}
/** Compute the expiry timestamp (ms) from a JWT's exp claim. */
export function computeExpiresAt(accessToken) {
    const payload = parseJwt(accessToken);
    return payload.exp * 1000;
}
//# sourceMappingURL=token-utils.js.map